import React from "react";

import { UseFormReturn, useController } from "react-hook-form";

import { FixedSimpleDropdown } from "src/base-components/FixedSimpleDropdown";
import { Pill } from "src/base-components/Pill";
import {
  CaseCreationNodeMetaAdvancedSettingsLiveEnum,
  CaseCreationNodeMetaAdvancedSettingsSandboxDataSourceEnum,
  CaseCreationNodeMetaAdvancedSettingsTestRunDataSourceEnum,
} from "src/clients/flow-api";
import { CaseCreationNodeData } from "src/constants/NodeDataTypes";
import { Callout } from "src/design-system/Callout";
import { useIsIcebergLongTestRunsEnabled } from "src/hooks/useIsIcebergLongTestRunsEnabled";

const TEST_RUN_OPTIONS: Array<{
  key: CaseCreationNodeMetaAdvancedSettingsTestRunDataSourceEnum;
  value: string;
}> = [
  {
    key: CaseCreationNodeMetaAdvancedSettingsTestRunDataSourceEnum.SANDBOX,
    value: "Create sandbox case",
  },
  {
    key: CaseCreationNodeMetaAdvancedSettingsTestRunDataSourceEnum.MOCK_DATA,
    value: "Use mock data",
  },
  {
    key: CaseCreationNodeMetaAdvancedSettingsTestRunDataSourceEnum.EMPTY_RESPONSE,
    value: "Empty response",
  },
];

const SANDBOX_OPTIONS: Array<{
  key: CaseCreationNodeMetaAdvancedSettingsSandboxDataSourceEnum;
  value: string;
}> = [
  {
    key: CaseCreationNodeMetaAdvancedSettingsSandboxDataSourceEnum.SANDBOX,
    value: "Create sandbox case",
  },
  {
    key: CaseCreationNodeMetaAdvancedSettingsSandboxDataSourceEnum.MOCK_DATA,
    value: "Use mock data",
  },
  {
    key: CaseCreationNodeMetaAdvancedSettingsSandboxDataSourceEnum.EMPTY_RESPONSE,
    value: "Empty response",
  },
];

const LIVE_OPTIONS = [
  {
    key: CaseCreationNodeMetaAdvancedSettingsLiveEnum.LIVE,
    value: "Create live case",
  },
];

export const DEFAULT_TEST_RUN_DATA_SOURCE =
  CaseCreationNodeMetaAdvancedSettingsTestRunDataSourceEnum.MOCK_DATA;

// Nodes saved before this setting existed create a real sandbox case.
export const DEFAULT_SANDBOX_DATA_SOURCE =
  CaseCreationNodeMetaAdvancedSettingsSandboxDataSourceEnum.SANDBOX;

type ConfigRowProps = {
  title: string;
  description: React.ReactNode;
  children: React.ReactNode;
};

const ConfigRow: React.FC<ConfigRowProps> = ({
  title,
  description,
  children,
}) => (
  <div className="flex w-full flex-row">
    <div className="flex grow flex-col">
      <h3 className="font-inter-medium-12px text-gray-800">{title}</h3>
      <span className="font-inter-normal-12px text-gray-500">
        {description}
      </span>
    </div>
    <div className="max-w-50 grow">{children}</div>
  </div>
);

type Props = {
  formMethods: UseFormReturn<CaseCreationNodeData>;
  immutable: boolean;
};

export const CaseCreationEnvironmentConfig: React.FC<Props> = ({
  formMethods,
  immutable,
}) => {
  const isIcebergLongTestRunsEnabled = useIsIcebergLongTestRunsEnabled();
  const { field: testRunField } = useController({
    control: formMethods.control,
    name: "advanced_settings.test_run_data_source",
  });
  const { field: sandboxField } = useController({
    control: formMethods.control,
    name: "advanced_settings.sandbox_data_source",
  });
  const selectedTestRunValue =
    testRunField.value ?? DEFAULT_TEST_RUN_DATA_SOURCE;
  const selectedSandboxValue =
    sandboxField.value ?? DEFAULT_SANDBOX_DATA_SOURCE;

  // Test runs only create Sandbox Cases when they run long, so without long test
  // runs the option and its callout are hidden. A node that already stores the
  // option keeps it, otherwise the dropdown would fall back to its placeholder.
  const isSandboxTestRunAvailable =
    isIcebergLongTestRunsEnabled ||
    selectedTestRunValue ===
      CaseCreationNodeMetaAdvancedSettingsTestRunDataSourceEnum.SANDBOX;
  const testRunOptions = isSandboxTestRunAvailable
    ? TEST_RUN_OPTIONS
    : TEST_RUN_OPTIONS.filter(
        (option) =>
          option.key !==
          CaseCreationNodeMetaAdvancedSettingsTestRunDataSourceEnum.SANDBOX,
      );

  return (
    <div className="flex flex-col gap-y-3">
      {selectedTestRunValue ===
        CaseCreationNodeMetaAdvancedSettingsTestRunDataSourceEnum.SANDBOX && (
        <Callout variant="neutral">
          Sandbox Cases are created only during long-running test runs, and
          those Cases aren&apos;t linked to a decision. Test runs started from
          the Decision Flow use mock data, so no Cases are created.
        </Callout>
      )}
      <ConfigRow
        description="Test runs using the button in the UI"
        title="For test runs, use"
      >
        <FixedSimpleDropdown
          buttonDataLoc="case-creation-test-run-data-source-button"
          dataLoc="case-creation-test-run-data-source"
          disabled={immutable}
          elements={testRunOptions}
          itemsWidth="w-full"
          placeholder="Select data source"
          selectedKey={selectedTestRunValue}
          onSelect={testRunField.onChange}
        />
      </ConfigRow>
      <ConfigRow
        description={
          <>
            API calls using the{" "}
            <Pill size="sm" variant="gray">
              <Pill.Text>/sandbox/decide</Pill.Text>
            </Pill>{" "}
            endpoint
          </>
        }
        title="For sandbox API calls, use"
      >
        <FixedSimpleDropdown
          buttonDataLoc="case-creation-sandbox-data-source-button"
          dataLoc="case-creation-sandbox-data-source"
          disabled={immutable}
          elements={SANDBOX_OPTIONS}
          itemsWidth="w-full"
          placeholder="Select data source"
          selectedKey={selectedSandboxValue}
          onSelect={sandboxField.onChange}
        />
      </ConfigRow>
      <ConfigRow
        description={
          <>
            Production API calls using the{" "}
            <Pill size="sm" variant="gray">
              <Pill.Text>/decide</Pill.Text>
            </Pill>{" "}
            endpoint
          </>
        }
        title="For live API calls, use"
      >
        <FixedSimpleDropdown
          buttonDataLoc="case-creation-live-data-source-button"
          dataLoc="case-creation-live-data-source"
          elements={LIVE_OPTIONS}
          itemsWidth="w-full"
          placeholder="Create live case"
          selectedKey={CaseCreationNodeMetaAdvancedSettingsLiveEnum.LIVE}
          disabled
          onSelect={() => undefined}
        />
      </ConfigRow>
    </div>
  );
};
